// React
import React, { useEffect, useRef, useState } from 'react';
// Styles
import './styles.scss';
import Select from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';

/**
 * Config for the react-select dropdowns
 * 
 * @returns {Object} config object
 */
const objConfig = {
    classNames: (props) => {
        return {
            control: (state) => (props.controlClassName ? props.controlClassName : '') + ' form-control',
        };
    },

    components: {
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
    },

    styles: {
        menu: (styles) => ({ ...styles, marginTop: 0 }),
        valueContainer: (styles) => ({ ...styles, paddingLeft: 0, marginLeft: '-2px' }),
        option: (styles, state) => ({
            ...styles,
            padding: '10px 20px',
            backgroundColor: (state.isFocused || state.isSelected) ? '#5eb5c1' : styles.backgroundColor,
            cursor: 'pointer',
        }),
        control: (styles, state) => ({
            ...styles,
            border: styles.border,
            boxShadow: state.isFocused ? '0 0 0 0.1rem #5eb5c1' : styles.boxShadow
        }),
    },
};

const CustomSelect = ({ async = false, ...props }) => {
    const dropdownRef = useRef(props.ref);
    const [value, setValue] = useState(props.value);
    const [loading, setLoading] = useState(props.isLoading);
    const options = props.options.map((option) => {
        return {
            ...option,
            value: option.value,
            label: option.name || option.label,
        }
    });

    /**
     * Scrolls to the selected options after a brief timeout after the options are rendered
     */
    const scrollToSelectedOption = () => {
        setTimeout(() => {
            const selectedEl = document.getElementsByClassName("custom-select__option--is-selected")[0];

            if (selectedEl) {
                // If the dropdown ref is available, try to focus on the selected option before scrolling to view
                if (dropdownRef?.current) {
                    dropdownRef.current.scrollToFocusedOptionOnUpdate = true;
                    dropdownRef.current.setState({
                        focusedOption: dropdownRef.current.getFocusableOptions().find(option => option.value === value), // We need to filter from the focusable options
                    });
                }

                selectedEl.scrollIntoView({behavior:'auto', block:'nearest', inline: 'start'});
            }
        }, 50);
    }

    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    useEffect(() => {
        setLoading(props.isLoading);

        // If the updated isLoading prop is false, scroll to the option
        if (props.isLoading === false) {
            scrollToSelectedOption();
        }
    }, [props.isLoading]);

    return async ? (
        <AsyncPaginate
            isLoading={loading}
            defaultOptions={props.autoload ?? false}
            loadOptions={props.loadOptions}
            additional={{ page: 1 }}
            value={(() => {
                // This is a self invoking function that determines the selected option

                // Try to filter the selected option from the options based on the value
                let filterValue = options.filter((option) => {
                    return option.value === value;
                });

                // If there is no filtered value from the options, assign an array containing the selected value as an option
                if (filterValue.length === 0) {
                    filterValue = [{
                        value: value,
                        label: value,
                    }];
                }

                return filterValue;
            })()}
            isSearchable={props.search}
            selectRef={dropdownRef}
            name={props.name}
            placeholder={props.placeholder ? props.placeholder : ''}
            classNamePrefix="custom-select"
            classNames={objConfig.classNames(props)}
            components={objConfig.components}
            onChange={e => props.onChange(e.value)}
            isDisabled={props.disabled}
            styles={objConfig.styles}
            onKeyDown={e => props.onKeyDown(e)}
            loadOptionsOnMenuOpen={true}
            getOptionValue={option => option.label}
            onMenuOpen={(e) => {
                // If the loading is finished, scroll to the option
                if (!loading) {
                    scrollToSelectedOption();
                }
            }}
            shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
                // This ensures that the next page of options are retrieved when scrolling 2/3 through the current list
                return (((scrollHeight - clientHeight) * 2) / 3) < scrollTop;
            }}
        />
    ) : (
        <Select
            value={options.filter((option) => {
                return option.value === value;
            })}
            isSearchable={props.search}
            selectRef={dropdownRef}
            name={props.name}
            placeholder={props.placeholder ? props.placeholder : ''}
            classNamePrefix="custom-select"
            options={options}
            classNames={objConfig.classNames(props)}
            components={objConfig.components}
            onChange={e => props.onChange(e.value)}
            isDisabled={props.disabled}
            styles={objConfig.styles}
            onKeyDown={e => props.onKeyDown(e)}
            getOptionValue={option => option.label}
        />
    );
};

export default CustomSelect;
